import { useEffect, useState } from 'react';
import { Router, Switch, Route } from 'react-router-dom';
import get from 'lodash.get';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';

import PrivateRoute from './private_route';

import SignIn from 'pages/sign_in';
import SignUp from 'pages/sign_up';
import Profile from 'pages/profile';

import NotFound from 'pages/not_found';

// Admin routes
import NurseTeleconsultationPage from 'pages/teleconsultation/nurse';

import Invitation from 'pages/invitation';
import ResetPassword from 'pages/reset_password';
import ForgotPassword from 'pages/forgot_password';

import Aside from '../aside/aside';
import MobileNavbar from '../mobile_navbar/mobile_navbar';
import Notification from '../notification/notification';

import IntellioAccountCreate from 'pages/intellio/intellio_account_create';

// Stripe routes
import CheckoutPage from 'pages/checkout';
import StripeRefreshOnboardingPage from 'pages/stripe_refresh_onboarding';

// Roles
import { roles } from 'config/roles';
import { userShape } from 'helpers/shared_types';

const NoAccess = () => {
  return (
    <div
      style={{
        minHeight: '60vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        color: '#555',
        fontSize: '1.8rem',
        textAlign: 'center',
        margin: '0 auto',
      }}
    >
      <p>Cette application a été archivée et n&apos;est plus accessible.</p>
      <button
        className="mr5 btn-green flex items-center"
        onClick={() => {
          localStorage.removeItem('authToken');
          window.location.href = '/signin';
        }}
      >
        Sign out
      </button>
    </div>
  );
};

const AppRouter = ({
  history,
  user,
  errors,
  onSignOut,
  onSignIn,
  onSignUp,
}) => {
  const [updateCount, setUpdateCount] = useState(false);
  const [showAside, setShowAside] = useState(false);

  const onCountUpdate = () => {
    setUpdateCount(!updateCount);
  };

  useEffect(() => {
    setShowAside(
      !!user &&
        !history.location.pathname.includes('invitation') &&
        !history.location.pathname.includes('reset_password') &&
        !history.location.pathname.includes('public') &&
        !history.location.pathname.includes('checkout'),
    );
  }, [history.location.pathname, user]);

  const onMessages = (messages) => {
    if (!messages || messages?.length === 0) return;
    const opts = {
      style: {
        background: '#ff5252',
      },
    };

    messages.forEach((message) => {
      switch (message.type) {
        case 'success':
          toast(message.text);
          break;
        case 'failure':
          toast.error(message.text, opts);
          break;
        default:
          toast.error('The request has failed.', opts);
          break;
      }
    });
  };

  const renderLandingPage = (user, props, errors) => {
    const componentProps = { ...props, onCountUpdate, onMessages, onSignIn };
    switch (user?.role) {
      case 'admin':
        return <NurseTeleconsultationPage {...componentProps} />;
      case 'nurse':
      case 'doctor':
      case 'technician':
      case 'psadRep':
        return <NoAccess />;
      default:
        return <SignIn {...componentProps} errors={errors} />;
    }
  };

  const renderComponentWithRoles = ({ user, entity, page }) => {
    if (user?.role !== 'admin') return NoAccess;
    return get(roles, [user?.role, entity, page], () => <></>);
  };

  return (
    <Router history={history}>
      <Notification />
      <div className="th-viewport">
        {showAside && <MobileNavbar history={history} onSignOut={onSignOut} />}
        {showAside && <Aside updateCount={updateCount} onSignOut={onSignOut} />}
        <Switch>
          <Route
            exact
            path="/"
            render={(props) => renderLandingPage(user, props, errors)}
          />
          <Route
            exact
            path="/signup"
            render={(props) => <SignUp {...props} onSignUp={onSignUp} />}
          />
          <Route
            exact
            path="/signin"
            render={(props) => (
              <SignIn {...props} errors={errors} onSignIn={onSignIn} />
            )}
          />
          <Route
            path="/invitation/:token?"
            render={(props) => (
              <Invitation
                {...props}
                onSignIn={onSignIn}
                onSignOut={onSignOut}
              />
            )}
          />
          <Route
            path="/reset_password/:token?"
            render={(props) => (
              <ResetPassword
                {...props}
                onSignIn={onSignIn}
                onSignOut={onSignOut}
              />
            )}
          />
          <Route
            path="/forgot_password"
            render={(props) => (
              <ForgotPassword
                {...props}
                onSignIn={onSignIn}
                onSignOut={onSignOut}
              />
            )}
          />
          <Route exact path="/checkout/" component={CheckoutPage} />
          <PrivateRoute
            onSignIn={onSignIn}
            path="/profile"
            component={Profile}
          />
          <PrivateRoute
            onSignIn={onSignIn}
            path="/intellio_account_create"
            component={IntellioAccountCreate}
          />
          <PrivateRoute
            onSignIn={onSignIn}
            path="/refresh_onboarding/"
            component={StripeRefreshOnboardingPage}
          />
          <PrivateRoute
            exact
            onSignIn={onSignIn}
            path="/nurse_teleconsultations"
            onCountUpdate={onCountUpdate}
            onMessages={onMessages}
            component={renderComponentWithRoles({
              user,
              entity: 'teleconsultation',
              page: 'nurseTeleconsultations',
            })}
          />
          <PrivateRoute
            exact
            onSignIn={onSignIn}
            path="/first_teleconsultations"
            onCountUpdate={onCountUpdate}
            onMessages={onMessages}
            component={renderComponentWithRoles({
              user,
              entity: 'teleconsultation',
              page: 'firstTeleconsultations',
            })}
          />
          <PrivateRoute
            exact
            onSignIn={onSignIn}
            path="/second_teleconsultations"
            onCountUpdate={onCountUpdate}
            onMessages={onMessages}
            component={renderComponentWithRoles({
              user,
              entity: 'teleconsultation',
              page: 'secondTeleconsultations',
            })}
          />
          <PrivateRoute
            exact
            onSignIn={onSignIn}
            path="/teleconsultations/:id(\d+)"
            onCountUpdate={onCountUpdate}
            onMessages={onMessages}
            component={renderComponentWithRoles({
              user,
              entity: 'teleconsultation',
              page: 'item',
            })}
          />
          <PrivateRoute
            exact
            onSignIn={onSignIn}
            path="/teleconsultations"
            component={renderComponentWithRoles({
              user,
              entity: 'teleconsultation',
              page: 'list',
            })}
          />
          <PrivateRoute
            exact
            onSignIn={onSignIn}
            path="/polygraphies"
            onCountUpdate={onCountUpdate}
            onMessages={onMessages}
            component={renderComponentWithRoles({
              user,
              entity: 'polygraphy',
              page: 'index',
            })}
          />
          <PrivateRoute
            exact
            onSignIn={onSignIn}
            onCountUpdate={onCountUpdate}
            onMessages={onMessages}
            path="/polygraphies/:id/edit"
            component={renderComponentWithRoles({
              user,
              entity: 'polygraphy',
              page: 'edit',
            })}
          />
          <PrivateRoute
            exact
            onSignIn={onSignIn}
            onCountUpdate={onCountUpdate}
            onMessages={onMessages}
            path="/polygraphies/add"
            component={renderComponentWithRoles({
              user,
              entity: 'polygraphy',
              page: 'add',
            })}
          />
          <PrivateRoute
            exact
            onSignIn={onSignIn}
            path="/polygraphies/:id(\d+)"
            component={renderComponentWithRoles({
              user,
              entity: 'polygraphy',
              page: 'item',
            })}
          />
          <PrivateRoute
            exact
            onSignIn={onSignIn}
            onCountUpdate={onCountUpdate}
            onMessages={onMessages}
            path="/polysomnographies"
            component={renderComponentWithRoles({
              user,
              entity: 'polysomnography',
              page: 'index',
            })}
          />
          <PrivateRoute
            exact
            onSignIn={onSignIn}
            onCountUpdate={onCountUpdate}
            onMessages={onMessages}
            path="/polysomnographies/add"
            component={renderComponentWithRoles({
              user,
              entity: 'polysomnography',
              page: 'add',
            })}
          />
          <PrivateRoute
            exact
            onSignIn={onSignIn}
            onCountUpdate={onCountUpdate}
            onMessages={onMessages}
            path="/polysomnographies/:id/edit"
            component={renderComponentWithRoles({
              user,
              entity: 'polysomnography',
              page: 'edit',
            })}
          />
          <PrivateRoute
            exact
            onSignIn={onSignIn}
            path="/polysomnographies/:id(\d+)"
            component={renderComponentWithRoles({
              user,
              entity: 'polysomnography',
              page: 'item',
            })}
          />
          <PrivateRoute
            exact
            onSignIn={onSignIn}
            path="/treatments/add"
            onCountUpdate={onCountUpdate}
            onMessages={onMessages}
            component={renderComponentWithRoles({
              user,
              entity: 'treatment',
              page: 'add',
            })}
          />
          <PrivateRoute
            onSignIn={onSignIn}
            path="/treatments/:id/edit"
            onCountUpdate={onCountUpdate}
            onMessages={onMessages}
            component={renderComponentWithRoles({
              user,
              entity: 'treatment',
              page: 'edit',
            })}
          />
          <PrivateRoute
            onSignIn={onSignIn}
            path="/treatments/:id(\d+)"
            onMessages={onMessages}
            component={renderComponentWithRoles({
              user,
              entity: 'treatment',
              page: 'item',
            })}
          />
          <PrivateRoute
            exact
            onSignIn={onSignIn}
            path="/treatments"
            onCountUpdate={onCountUpdate}
            onMessages={onMessages}
            component={renderComponentWithRoles({
              user,
              entity: 'treatment',
              page: 'list',
            })}
          />
          <PrivateRoute
            exact
            onSignIn={onSignIn}
            path="/renewals"
            onCountUpdate={onCountUpdate}
            onMessages={onMessages}
            component={renderComponentWithRoles({
              user,
              entity: 'teleconsultation',
              page: 'renewalTeleconsultation',
            })}
          />
          <PrivateRoute
            exact
            onSignIn={onSignIn}
            path="/doctors/add"
            component={renderComponentWithRoles({
              user,
              entity: 'doctor',
              page: 'add',
            })}
          />
          <PrivateRoute
            onSignIn={onSignIn}
            path="/doctors/:id/edit"
            component={renderComponentWithRoles({
              user,
              entity: 'doctor',
              page: 'edit',
            })}
          />
          <PrivateRoute
            onSignIn={onSignIn}
            path="/doctors/:id(\d+)"
            component={renderComponentWithRoles({
              user,
              entity: 'doctor',
              page: 'item',
            })}
          />
          <PrivateRoute
            exact
            onSignIn={onSignIn}
            path="/doctors"
            component={renderComponentWithRoles({
              user,
              entity: 'doctor',
              page: 'list',
            })}
          />
          <PrivateRoute
            exact
            onSignIn={onSignIn}
            path="/technicians/add"
            component={renderComponentWithRoles({
              user,
              entity: 'technician',
              page: 'add',
            })}
          />
          <PrivateRoute
            onSignIn={onSignIn}
            path="/technicians/:id/edit"
            component={renderComponentWithRoles({
              user,
              entity: 'technician',
              page: 'edit',
            })}
          />
          <PrivateRoute
            onSignIn={onSignIn}
            path="/technicians/:id(\d+)"
            component={renderComponentWithRoles({
              user,
              entity: 'technician',
              page: 'item',
            })}
          />
          <PrivateRoute
            exact
            onSignIn={onSignIn}
            path="/technicians"
            component={renderComponentWithRoles({
              user,
              entity: 'technician',
              page: 'list',
            })}
          />
          <PrivateRoute
            exact
            onSignIn={onSignIn}
            path="/customers/:id(\d+)"
            onMessages={onMessages}
            component={renderComponentWithRoles({
              user,
              entity: 'customer',
              page: 'item',
            })}
          />
          <PrivateRoute
            exact
            onSignIn={onSignIn}
            path="/customers"
            component={renderComponentWithRoles({
              user,
              entity: 'customer',
              page: 'list',
            })}
          />
          <PrivateRoute
            onSignIn={onSignIn}
            path="/psad_reps/add"
            component={renderComponentWithRoles({
              user,
              entity: 'psadRep',
              page: 'add',
            })}
          />
          <PrivateRoute
            onSignIn={onSignIn}
            path="/psad_reps/:id/edit"
            component={renderComponentWithRoles({
              user,
              entity: 'psadRep',
              page: 'edit',
            })}
          />
          <PrivateRoute
            onSignIn={onSignIn}
            path="/psad_reps/:id(\d+)"
            component={renderComponentWithRoles({
              user,
              entity: 'psadRep',
              page: 'item',
            })}
          />
          <PrivateRoute
            exact
            onSignIn={onSignIn}
            path="/psad_reps"
            component={renderComponentWithRoles({
              user,
              entity: 'psadRep',
              page: 'list',
            })}
          />
          <PrivateRoute
            onSignIn={onSignIn}
            path="/responses/:id/edit"
            component={renderComponentWithRoles({
              user,
              entity: 'response',
              page: 'edit',
            })}
          />
          <PrivateRoute
            onSignIn={onSignIn}
            path="/responses/:id(\d+)"
            component={renderComponentWithRoles({
              user,
              entity: 'response',
              page: 'item',
            })}
          />
          <PrivateRoute
            exact
            onSignIn={onSignIn}
            path="/responses"
            component={renderComponentWithRoles({
              user,
              entity: 'response',
              page: 'list',
            })}
          />

          <PrivateRoute
            onSignIn={onSignIn}
            path="/tasks/add"
            component={renderComponentWithRoles({
              user,
              entity: 'task',
              page: 'add',
            })}
          />
          <PrivateRoute
            onSignIn={onSignIn}
            onCountUpdate={onCountUpdate}
            onMessages={onMessages}
            path="/tasks"
            component={renderComponentWithRoles({
              user,
              entity: 'task',
              page: 'list',
            })}
          />

          {/* Calendar routes */}
          <PrivateRoute
            exact
            onSignIn={onSignIn}
            path="/calendar/exams"
            onCountUpdate={onCountUpdate}
            onMessages={onMessages}
            component={renderComponentWithRoles({
              user,
              entity: 'calendar',
              page: 'exams',
            })}
          />

          {/* Doctor routes */}

          <PrivateRoute
            onSignIn={onSignIn}
            path="/calendar/teleconsultations"
            component={renderComponentWithRoles({
              user,
              entity: 'calendar',
              page: 'teleconsultations',
            })}
          />
          <PrivateRoute
            onSignIn={onSignIn}
            path="/patients"
            component={renderComponentWithRoles({
              user,
              entity: 'customer',
              page: 'patients',
            })}
          />
          <PrivateRoute
            onSignIn={onSignIn}
            onMessages={onMessages}
            path="/referrals/add"
            component={renderComponentWithRoles({
              user,
              entity: 'referral',
              page: 'add',
            })}
          />
          <PrivateRoute
            onSignIn={onSignIn}
            path="/referrals/:id(\d+)"
            component={renderComponentWithRoles({
              user,
              entity: 'referral',
              page: 'item',
            })}
          />
          <PrivateRoute
            onSignIn={onSignIn}
            path="/documents/generate-dap"
            component={renderComponentWithRoles({
              user,
              entity: 'document',
              page: 'generateDAP',
            })}
          />
          <PrivateRoute
            onSignIn={onSignIn}
            onMessages={onMessages}
            path="/documents/:id/edit"
            component={renderComponentWithRoles({
              user,
              entity: 'document',
              page: 'edit',
            })}
          />
          <PrivateRoute
            onSignIn={onSignIn}
            path="/documents/:id/update"
            component={renderComponentWithRoles({
              user,
              entity: 'document',
              page: 'update',
            })}
          />
          <PrivateRoute
            onSignIn={onSignIn}
            path="/documents/:id(\d+)"
            component={renderComponentWithRoles({
              user,
              entity: 'document',
              page: 'item',
            })}
          />
          <PrivateRoute
            onSignIn={onSignIn}
            path="/invoices/add"
            component={renderComponentWithRoles({
              user,
              entity: 'invoice',
              page: 'add',
            })}
          />
          <PrivateRoute
            onSignIn={onSignIn}
            path="/invoices"
            component={renderComponentWithRoles({
              user,
              entity: 'invoice',
              page: 'list',
            })}
          />

          <Route
            render={(props) =>
              user ? <NotFound /> : <SignIn {...props} onSignIn={onSignIn} />
            }
          />
        </Switch>
      </div>
    </Router>
  );
};

AppRouter.propTypes = {
  history: ReactRouterPropTypes.history.isRequired,
  user: userShape,
  onSignOut: PropTypes.func.isRequired,
  onSignIn: PropTypes.func.isRequired,
  onSignUp: PropTypes.func,
  errors: PropTypes.arrayOf(
    PropTypes.shape({
      message: PropTypes.string,
    }),
  ),
};

export default AppRouter;
